class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll('button:not(.localization-selector)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const clickedElement = event.target;
    const clickedIndex = clickedElement.getAttribute('data-index');
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement, clickedIndex);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement, clickedIndex = false) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    if (clickedIndex) {
      const detailsElements = document.querySelectorAll('.mobile-facets__details');
      detailsElements.forEach((element) => {
        if (element.getAttribute('data-index') === clickedIndex) {
          setTimeout(() => {
            element.setAttribute('open', '');
          }, 100);
        }
      });
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class TabsComponent extends HTMLElement {
  constructor() {
    super();
    this.tabList = this.querySelector('[role="tablist"]');
    if (!this.tabList) return;

    this.tabList.addEventListener('click', this.onTabClick.bind(this));
    this.tabList.addEventListener('keydown', this.onArrowKeyDown.bind(this));
    this.tabList.querySelectorAll('[role="tab"]').forEach((tab) => {
      tab.addEventListener('click', this.onTabClick.bind(this));
    });
  }

  onTabClick(event) {
    const tab = event.target;
    const panelId = tab.getAttribute('aria-controls');
    if (!panelId) return;

    this.tabList.querySelectorAll('[role="tab"]').forEach((t) => {
      t.setAttribute('aria-selected', t === tab);
    });
    this.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      panel.toggleAttribute('hidden', panel.id !== panelId);
    });
  }

  onArrowKeyDown(event) {
    const tab = event.target;
    if (event.code === 'ArrowRight') {
      const nextTab = tab.nextElementSibling;
      if (nextTab) nextTab.focus();
    } else if (event.code === 'ArrowLeft') {
      const prevTab = tab.previousElementSibling;
      if (prevTab) prevTab.focus();
    }
  }
}
customElements.define('tabs-component', TabsComponent);
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    // Fix for nested sliders
    const sliderSelector = this.slider?.getAttribute('data-slide-selector');
    if (sliderSelector) {
      this.sliderItems = this.querySelectorAll(`.slider__slide.${sliderSelector}`);
      this.prevButton = this.querySelector(`button[name="previous"].${sliderSelector}`);
      this.nextButton = this.querySelector(`button[name="next"].${sliderSelector}`);
    } else {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
    }
    this.enableSliderLooping = true;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');

    // Orientation: horizontal (default) or vertical
    this.orientation = this.getAttribute('data-orientation') === 'vertical' ? 'vertical' : 'horizontal';

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton && this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton && this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) =>
      this.orientation === 'vertical' ? element.clientHeight > 0 : element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    if (this.orientation === 'vertical') {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop;
      this.slidesPerPage = Math.round(
        (this.slider.clientHeight - this.sliderItemsToShow[0].offsetTop) / this.sliderItemOffset
      );
    } else {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.round(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
      );
    }
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider) return;

    const previousPage = this.currentPage;
    if (this.orientation === 'vertical') {
      this.currentPage = Math.round(this.slider.scrollTop / this.sliderItemOffset) + 1;
    } else {
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    }

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    // Handle infinite looping for horizontal sliders - never disable buttons
    if (this.enableSliderLooping && this.orientation === 'horizontal') {
      this.prevButton && this.prevButton.removeAttribute('disabled');
      this.nextButton && this.nextButton.removeAttribute('disabled');
      this.classList.remove('at-start');
      this.classList.remove('at-end');
      return;
    }

    // Only apply at-start/at-end if slides overflow the parent container
    let isOverflowing = false;
    if (this.orientation === 'vertical') {
      isOverflowing = this.slider.scrollHeight > this.slider.clientHeight;
    } else {
      isOverflowing = this.slider.scrollWidth > this.slider.clientWidth;
    }

    let isAnnouncementBar = this.slider.classList.contains('slider--announcements');

    if (isOverflowing) {
      // At start
      if (
        this.isSlideVisible(this.sliderItemsToShow[0]) &&
        ((this.orientation === 'vertical' && this.slider.scrollTop === 0) ||
          (this.orientation === 'horizontal' && this.slider.scrollLeft === 0)) &&
        !isAnnouncementBar
      ) {
        this.prevButton && this.prevButton.setAttribute('disabled', 'disabled');
        this.classList.add('at-start');
      } else {
        this.prevButton && this.prevButton.removeAttribute('disabled');
        this.classList.remove('at-start');
      }

      // At end
      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]) && !isAnnouncementBar) {
        this.nextButton && this.nextButton.setAttribute('disabled', 'disabled');
        this.classList.add('at-end');
      } else {
        this.nextButton && this.nextButton.removeAttribute('disabled');
        this.classList.remove('at-end');
      }
    } else {
      // If not overflowing, always remove at-start/at-end and enable buttons
      this.prevButton && this.prevButton.setAttribute('disabled', 'disabled');
      this.nextButton && this.nextButton.setAttribute('disabled', 'disabled');
      this.classList.remove('at-start');
      this.classList.remove('at-end');
    }
  }

  isSlideVisible(element, offset = 0) {
    if (this.orientation === 'vertical') {
      const lastVisibleSlide = this.slider.clientHeight + this.slider.scrollTop - offset;
      return (
        element.offsetTop + element.clientHeight - 1 <= lastVisibleSlide && element.offsetTop >= this.slider.scrollTop
      );
    } else {
      const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
      return (
        element.offsetLeft + element.clientWidth - 1 <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft
      );
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;

    if (this.enableSliderLooping) {
      // Handle infinite looping
      if (this.orientation === 'vertical') {
        if (event.currentTarget.name === 'next') {
          // If at the end, go to the beginning
          if (this.currentPage >= this.totalPages) {
            this.setSlidePosition(0);
          } else {
            this.slideScrollPosition = this.slider.scrollTop + step * this.sliderItemOffset;
            this.setSlidePosition(this.slideScrollPosition);
          }
        } else {
          // If at the beginning, go to the end
          if (this.currentPage <= 1) {
            this.setSlidePosition((this.totalPages - 1) * this.sliderItemOffset);
          } else {
            this.slideScrollPosition = this.slider.scrollTop - step * this.sliderItemOffset;
            this.setSlidePosition(this.slideScrollPosition);
          }
        }
      } else {
        if (event.currentTarget.name === 'next') {
          // If at the end, go to the beginning
          if (this.currentPage >= this.totalPages) {
            this.setSlidePosition(0);
          } else {
            this.slideScrollPosition = this.slider.scrollLeft + step * this.sliderItemOffset;
            this.setSlidePosition(this.slideScrollPosition);
          }
        } else {
          // If at the beginning, go to the end
          if (this.currentPage <= 1) {
            this.setSlidePosition((this.totalPages - 1) * this.sliderItemOffset);
          } else {
            this.slideScrollPosition = this.slider.scrollLeft - step * this.sliderItemOffset;
            this.setSlidePosition(this.slideScrollPosition);
          }
        }
      }
    } else {
      // Original non-looping behavior
      if (this.orientation === 'vertical') {
        this.slideScrollPosition =
          event.currentTarget.name === 'next'
            ? this.slider.scrollTop + step * this.sliderItemOffset
            : this.slider.scrollTop - step * this.sliderItemOffset;
        this.setSlidePosition(this.slideScrollPosition);
      } else {
        this.slideScrollPosition =
          event.currentTarget.name === 'next'
            ? this.slider.scrollLeft + step * this.sliderItemOffset
            : this.slider.scrollLeft - step * this.sliderItemOffset;
        this.setSlidePosition(this.slideScrollPosition);
      }
    }
  }

  setSlidePosition(position) {
    if (this.orientation === 'vertical') {
      this.slider.scrollTo({
        top: position,
      });
    } else {
      this.slider.scrollTo({
        left: position,
      });
    }
  }
}

customElements.define('slider-component', SliderComponent);
